import {
  LogoutRounded as LogoutRoundedIcon,
  WarningAmberRounded as WarningAmberRoundedIcon,
} from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import { Box, Stack, Typography } from '@mui/material'
import React, { useCallback, useState } from 'react'
import { T } from '@/helpers/T'
import { useAuthContext } from '@/providers/Global'

export const IMPERSONATION_BANNER_HEIGHT = 44

const ImpersonationBannerInternal: React.FC = () => {
  const Auth = useAuthContext()
  const [loading, setLoading] = useState(false)

  const impersonation = Auth.active?.impersonation

  const handleStop = useCallback(async () => {
    setLoading(true)
    try {
      await Auth.stopImpersonation()
    } finally {
      setLoading(false)
    }
  }, [Auth])

  if (!impersonation || !Auth.active) {
    return null
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: (theme) => theme.zIndex.appBar + 2,
        bgcolor: 'error.main',
        color: 'common.white',
        height: IMPERSONATION_BANNER_HEIGHT,
        px: { xs: 1.5, md: 2.5 },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
      }}
    >
      <Stack
        direction='row'
        spacing={{ xs: 1, md: 1.5 }}
        alignItems='center'
        sx={{ minWidth: 0, flex: 1 }}
      >
        <WarningAmberRoundedIcon fontSize='small' />
        <Typography
          variant='body2'
          sx={{
            fontWeight: 700,
            letterSpacing: 0.5,
            textTransform: 'uppercase',
            whiteSpace: 'nowrap',
            display: { xs: 'none', sm: 'inline' },
          }}
        >
          <T id='r.impersonation.banner_title' />
        </Typography>
        <Typography
          variant='body2'
          sx={{
            opacity: 0.95,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          <T
            id='r.impersonation.banner_subtitle'
            values={{
              target: Auth.active.user.fullName ?? Auth.active.username,
              original: impersonation.originalUser.fullName,
            }}
          />
        </Typography>
      </Stack>

      <LoadingButton
        type='button'
        variant='contained'
        size='small'
        loading={loading}
        startIcon={<LogoutRoundedIcon />}
        sx={{
          bgcolor: 'common.white',
          color: 'error.main',
          flexShrink: 0,
          '&:hover': { bgcolor: 'grey.100' },
        }}
        onClick={handleStop}
      >
        <T id='r.impersonation.stop_button' />
      </LoadingButton>
    </Box>
  )
}

export const ImpersonationBanner = React.memo(
  ImpersonationBannerInternal,
) as typeof ImpersonationBannerInternal
